import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { ENROL_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import { trackClientEvent } from 'analytics/analytics'
import { loyaltyContext } from 'analytics/snowplow/eventContexts/loyaltyContext'
import requestLoyaltyAccountEnrolment from 'api/loyalty/requestLoyaltyAccountEnrolment'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import config from 'constants/config'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import waitForLoyaltyAccountFetch from 'loyalty/lib/waitForLoyaltyAccountFetch'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import isValidLoyaltyRegion from 'api/lib/isValidLoyaltyRegion'
import { getCurrentIntl } from 'i18n/util/currentIntl'

type LoyaltyEnrolmentSource = paths['/api/lux-loyalty/account']['post']['requestBody']['content']['application/json']['enrolmentSource']

interface EnrolLoyaltyAccount {
  accountMemberId?: string
  token?: string
  enableSnackbar?: boolean
  enrolment?: App.LoyaltyEnrolmentType | undefined
  region?: string
  enrolmentSource?: LoyaltyEnrolmentSource
}

/**
 * Enrols a user in the loyalty program
 *
 * @param {EnrolLoyaltyAccount} params - The enrollment parameters
 * @param {string} [params.accountMemberId] - Optional member ID to enrol. If not provided, uses the current authenticated user's member ID
 * @param {string} [params.token] - Optional authentication token for the enrollment API request
 * @param {boolean} [params.enableSnackbar] - Whether to show snackbar notifications (currently unused in implementation)
 * @param {App.LoyaltyEnrolmentType} [params.enrolment] - The type of enrollment (e.g., 'newUserRegistration', 'existingUserRegistration')
 * @param {string} [params.region] - Optional region to enrol in. If not provided, uses the current site region
 * @param {LoyaltyEnrolmentSource} [params.enrolmentSource] - Optional source recorded by the loyalty service. Defaults to 'CUSTOMER' on the server. 'AUTO_ENROLLED' also skips the error snackbar, as the customer didn't ask to enrol
 *
 * @returns {AppAction} A Redux thunk action that dispatches the enrollment API call
 *
 * @description
 * This function handles the enrollment of users into the loyalty program. It:
 * - Validates that loyalty is enabled and the user doesn't already have an account
 * - Checks if the user's region is valid for loyalty enrollment
 * - Makes an API call to enrol the user with their member ID and region
 * - Shows appropriate success messages based on enrollment type
 * - Tracks analytics events for the enrollment
 * - Sends messages to mobile WebView if in headless mode
 *
 * If a loyalty account read or enrolment is in flight, e.g. the read started at sign-in when a
 * customer joins straight after signing in, it waits for that to settle before checking the rest.
 *
 * The function will exit early without taking action if:
 * - Loyalty is not enabled for the current state
 * - No member ID is available
 * - User already has a loyalty account
 * - A loyalty account fetch is still in progress after waiting, or has errored
 * - The user's region is not valid for loyalty
 *
 * @example
 * // Enrol current authenticated user
 * dispatch(enrolLoyaltyAccount({ enrolment: 'newUserRegistration' }))
 */
function enrolLoyaltyAccount({
  accountMemberId,
  token,
  enrolment,
  region,
  enrolmentSource,
}: EnrolLoyaltyAccount): AppAction {
  return async(dispatch, getState) => {
    if (getState().loyalty.account.fetching) {
      await waitForLoyaltyAccountFetch(getState)
    }

    const state = getState()
    const memberId = accountMemberId ?? state.auth.account.memberId
    const givenName = state.auth.account.givenName
    const headlessMode = state.system.headlessMode
    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const loyaltyAccount = state.loyalty.account
    const regionCode = region ?? state.geo.currentRegionCode

    let enrolled = false

    if (
      !getIsLoyaltyEnabled(state) ||
      !memberId ||
      hasLoyaltyAccount ||
      loyaltyAccount.fetching ||
      loyaltyAccount.error ||
      !isValidLoyaltyRegion(regionCode)
    ) return

    dispatch({
      type: API_CALL,
      api: ENROL_LOYALTY_ACCOUNT,
      enrolment,
      request: () => requestLoyaltyAccountEnrolment({
        customerId: memberId,
        region: regionCode,
        enrolmentSource,
      }, token)
        .then((result) => {
          const intl = getCurrentIntl()
          enrolled = true
          let analyticsAction = enrolmentSource === 'AUTO_ENROLLED' ? 'auto_enrol' : 'default'

          if (enrolment === 'newUserRegistration') {
            analyticsAction = 'sign_up'
            const loyaltyName = getLoyaltyProgramDisplayName()
            const welcomeMessage = givenName
              ? intl.formatMessage({
                  id: 'Ac56kCMxlGfjDbb-yrTx',
                  defaultMessage: 'Welcome to {title} and your new {loyaltyName} membership, {givenName}. Start earning today.',
                  description: 'Snackbar message welcoming a new user to loyalty program with their name',
                }, { title: config.title, loyaltyName, givenName })
              : intl.formatMessage({
                  id: 'Ec2GlHS2By8tlJJAQjQp',
                  defaultMessage: 'Welcome to {title} and your new {loyaltyName} membership. Start earning today.',
                  description: 'Snackbar message welcoming a new user to loyalty program',
                }, { title: config.title, loyaltyName })
            showSnackbar(welcomeMessage, 'success')
          }

          if (enrolment === 'existingUserRegistration') {
            analyticsAction = 'opt_in'
            const loyaltyName = getLoyaltyProgramDisplayName()
            const welcomeMessage = givenName
              ? intl.formatMessage({
                  id: 'Fyf6rRcwSezG-0OtfAX8',
                  defaultMessage: 'Welcome to your new {loyaltyName} membership, {givenName}. Start earning today.',
                  description: 'Snackbar message welcoming an existing user to loyalty program with their name',
                }, { loyaltyName, givenName })
              : intl.formatMessage({
                  id: 'KedLJn3Nx9krLQfhxhh5',
                  defaultMessage: 'Welcome to your new {loyaltyName} membership. Start earning today.',
                  description: 'Snackbar message welcoming an existing user to loyalty program',
                }, { loyaltyName })
            showSnackbar(welcomeMessage, 'success')
          }

          // No welcome here for an auto-enrolment: AutoEnrolledNoticeGate shows it once no modal is open,
          // as sign-in can open the mobile number prompt over it

          if (result?.account?.accountNumber) {
            trackClientEvent({
              action: analyticsAction,
              category: 'loyalty_sign_up',
              subject: result.account.accountNumber + '_' + result.account.tier,
              type: 'operational',
              options: {
                contextsToSkip: [
                  'loyalty',
                ],
              },
              context: [
                loyaltyContext(result.account),
              ],
            },
            )
          }
          return result
        })
        .catch((err) => {
          // The customer didn't ask to be auto-enrolled, so a failure isn't theirs to see
          if (enrolmentSource === 'AUTO_ENROLLED') throw err

          const intl = getCurrentIntl()
          showSnackbar(
            intl.formatMessage({
              id: 'OJOLxB1QiRFl-CPrRGD_',
              defaultMessage: 'Please try again later or contact us for assistance.',
              description: 'Snackbar body message when loyalty account enrolment fails',
            }),
            'critical',
            {
              heading: intl.formatMessage({
                id: 'TMIRgOTmHYXSa0nc6Ode',
                defaultMessage: 'Oops! Something went wrong',
                description: 'Snackbar heading when loyalty account enrolment fails',
              }),
            },
          )

          throw err
        })
        .finally(() => {
          // Send message to mobile WebView
          if (headlessMode) {
            appViewMessageHandler('luxLoyalty', { enrolled })
          }
        }),
    })
  }
}

export default enrolLoyaltyAccount
